import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import Badge from '../ui/badge';
import {
  Target,
  Clock,
  DollarSign,
  Star,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,

} from 'recharts';

const ModelComparisonMetrics = ({ results, comparisonName }) => {
  if (!results || !results.detailed_results) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No metrics data available</p>
      </div>
    );
  }

  const models = Object.keys(results.detailed_results);
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Prepare data for charts
  const metricsData = models.map((model, index) => {
    const data = results.detailed_results[model];
    return {
      model: model.split('/')[1] || model,
      fullModel: model,
      accuracy: data.accuracy || 0,
      latency: data.average_latency || 0,
      cost: data.total_cost || 0,
      quality: data.quality_score || 0,
      color: COLORS[index % COLORS.length]
    };
  });

  // Calculate rankings
  const rankings = {
    accuracy: [...metricsData].sort((a, b) => b.accuracy - a.accuracy),
    latency: [...metricsData].sort((a, b) => a.latency - b.latency), // Lower is better
    cost: [...metricsData].sort((a, b) => a.cost - b.cost), // Lower is better
    quality: [...metricsData].sort((a, b) => b.quality - a.quality)
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 0:
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case models.length - 1:
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRankBadge = (rank, total) => {
    const variants = {
      0: 'secondary', // Best
      [total - 1]: 'destructive' // Worst
    };

    return (
      <Badge variant={variants[rank] || 'outline'}>
        #{rank + 1}
      </Badge>
    );
  };

  const formatMetric = (value, type) => {
    switch (type) {
      case 'accuracy':
      case 'quality':
        return `${value.toFixed(1)}%`;
      case 'latency':
        return `${value.toFixed(2)}s`;
      case 'cost':
        return `$${value.toFixed(3)}`;
      default:
        return value.toString();
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Target className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">Best Accuracy</p>
                <p className="text-xl font-bold">
                  {formatMetric(rankings.accuracy[0]?.accuracy || 0, 'accuracy')}
                </p>
                <p className="text-xs text-gray-500">{rankings.accuracy[0]?.model}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Fastest Response</p>
                <p className="text-xl font-bold">
                  {formatMetric(rankings.latency[0]?.latency || 0, 'latency')}
                </p>
                <p className="text-xs text-gray-500">{rankings.latency[0]?.model}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-sm text-gray-600">Most Cost-Effective</p>
                <p className="text-xl font-bold">
                  {formatMetric(rankings.cost[0]?.cost || 0, 'cost')}
                </p>
                <p className="text-xs text-gray-500">{rankings.cost[0]?.model}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-600">Highest Quality</p>
                <p className="text-xl font-bold">
                  {formatMetric(rankings.quality[0]?.quality || 0, 'quality')}
                </p>
                <p className="text-xs text-gray-500">{rankings.quality[0]?.model}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Metrics Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {metricsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metricsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="model" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="accuracy" fill="#3b82f6" name="Accuracy %" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No data available for comparison
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accuracy Rankings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Accuracy Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rankings.accuracy.map((model, index) => (
                <div key={model.fullModel} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {getRankBadge(index, models.length)}
                    <span className="font-medium">{model.model}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {formatMetric(model.accuracy, 'accuracy')}
                    </span>
                    {getRankIcon(index)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Latency Rankings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Response Time Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rankings.latency.map((model, index) => (
                <div key={model.fullModel} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {getRankBadge(index, models.length)}
                    <span className="font-medium">{model.model}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {formatMetric(model.latency, 'latency')}
                    </span>
                    {getRankIcon(index)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cost Rankings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Cost Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rankings.cost.map((model, index) => (
                <div key={model.fullModel} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {getRankBadge(index, models.length)}
                    <span className="font-medium">{model.model}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {formatMetric(model.cost, 'cost')}
                    </span>
                    {getRankIcon(index)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quality Rankings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Quality Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rankings.quality.map((model, index) => (
                <div key={model.fullModel} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {getRankBadge(index, models.length)}
                    <span className="font-medium">{model.model}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {formatMetric(model.quality, 'quality')}
                    </span>
                    {getRankIcon(index)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost vs Performance Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Cost vs Performance Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {metricsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metricsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="model" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Bar yAxisId="left" dataKey="accuracy" fill="#3b82f6" name="Accuracy %" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cost"
                    stroke="#f59e0b"
                    strokeWidth={3}
                    name="Cost ($)"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No data available for analysis
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Model</th>
                  <th className="text-left p-3">Accuracy</th>
                  <th className="text-left p-3">Latency</th>
                  <th className="text-left p-3">Cost</th>
                  <th className="text-left p-3">Quality</th>
                  <th className="text-left p-3">Overall Rank</th>
                </tr>
              </thead>
              <tbody>
                {metricsData.map((model, index) => {
                  // Calculate overall rank based on normalized scores
                  const normalizedAccuracy = model.accuracy / Math.max(...metricsData.map(m => m.accuracy));
                  const normalizedLatency = Math.min(...metricsData.map(m => m.latency)) / model.latency;
                  const normalizedCost = Math.min(...metricsData.map(m => m.cost)) / model.cost;
                  const normalizedQuality = model.quality / Math.max(...metricsData.map(m => m.quality));

                  const overallScore = (normalizedAccuracy + normalizedLatency + normalizedCost + normalizedQuality) / 4;

                  return (
                    <tr key={model.fullModel} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{model.model}</td>
                      <td className="p-3">{formatMetric(model.accuracy, 'accuracy')}</td>
                      <td className="p-3">{formatMetric(model.latency, 'latency')}</td>
                      <td className="p-3">{formatMetric(model.cost, 'cost')}</td>
                      <td className="p-3">{formatMetric(model.quality, 'quality')}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${overallScore * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">
                            {(overallScore * 100).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModelComparisonMetrics;
